
import { makeAutoObservable } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { SessionService } from '@evertel/session/SessionService'
import { Api } from '@evertel/api'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import { DepartmentStore } from '@evertel/department'
import { getRole } from '@evertel/utils'

class CurrentUserDepartmentsController {

    constructor(
        private api: Api,
        private session: SessionService,
        private departmentsAccessStore: DepartmentsAccessStore,
        private departmentStore: DepartmentStore
    ) {
        makeAutoObservable(this)
    }

    get departmentsAccess() {
        return this.departmentsAccessStore.find(da => da.blueUserId === this.session.currentUserId)
    }

    get departments() {
        const departmentIds = this.departmentsAccess.map(da => da.departmentId)
        return this.departmentStore.findByIds(departmentIds)
    }

    get departmentsAccessNotGuest() {
        return this.departmentsAccess?.filter((da: any) => getRole(da.roles) !== 'guest')
    }

    get departmentsNotGuest() {
        const departmentIds = this.departmentsAccessNotGuest.map(da => da.departmentId)
        return this.departmentStore.findByIds(departmentIds)
    }

    get departmentsGuest() {
        const departmentsIds = this.departmentsAccess?.filter(da => getRole(da.roles) === 'guest').map(da => da.departmentId)
        return this.departmentStore.findByIds(departmentsIds)
    }

    get departmentsGuestVerified() {
        const departmentsIds = this.departmentsAccess?.filter(da => getRole(da.roles) === 'guest' && da.isVerified === true).map(da => da.departmentId)
        return this.departmentStore.findByIds(departmentsIds)
    }

    get departmentsNotGuestVerified() {
        const departmentsIds = this.departmentsAccess?.filter(da => getRole(da.roles) !== 'guest' && da.isVerified === true).map(da => da.departmentId)
        return this.departmentStore.findByIds(departmentsIds)
    }

    get departmentsPending() {
        const pendingIds = this.departmentsAccess.filter(da => da.isVerified !== true).map(da => da.departmentId)
        return this.departmentStore.findByIds(pendingIds)
    }

    get groupedDepartments() {
        const byId = (ids: number[]) => this.departmentStore.findByIds(ids)

        const acc = this.departmentsAccess
        const verifiedNonGuestIds = acc.filter(da => getRole(da.roles) !== 'guest' && da.isVerified === true).map(da => da.departmentId)
        const verifiedGuestIds    = acc.filter(da => getRole(da.roles) === 'guest'  && da.isVerified === true).map(da => da.departmentId)
        const pendingIds          = acc.filter(da => da.isVerified !== true).map(da => da.departmentId)

        return {
            verifiedNonGuest: byId(verifiedNonGuestIds),
            verifiedGuest: byId(verifiedGuestIds),
            pending: byId(pendingIds)
        }
    }

    get primaryDepartmentId() {
        return this.departmentsAccess.find(da => da.isPrimary === true)?.departmentId ?? null
    }


    getSortedDepartments(
        departments: any[],
        recentDeptIds: number[],
        roomUnreadCountsByDept: Record<number, number>,
        hasRoomUnreadUrgentsByDept: Record<number, boolean>
    ): any[] {
        return [...departments].sort((a, b) => {
            const aInRecent = recentDeptIds.indexOf(a.id)
            const bInRecent = recentDeptIds.indexOf(b.id)

            // 1. Sort by presence in recentDepartmentIds
            if (aInRecent !== -1 && bInRecent !== -1) {
                return aInRecent - bInRecent
            }
            if (aInRecent !== -1) return -1
            if (bInRecent !== -1) return 1

            // 2. Sort by urgent messages
            const aUrgent = hasRoomUnreadUrgentsByDept[a.id] || false
            const bUrgent = hasRoomUnreadUrgentsByDept[b.id] || false
            if (aUrgent && bUrgent) {
                return a.name.localeCompare(b.name)
            }
            if (aUrgent) return -1
            if (bUrgent) return 1

            // 3. Sort by non-urgent unread messages
            const aUnread = roomUnreadCountsByDept[a.id] || 0
            const bUnread = roomUnreadCountsByDept[b.id] || 0
            if (aUnread > 0 && bUnread > 0) {
                return a.name.localeCompare(b.name)
            }
            if (aUnread > 0) return -1
            if (bUnread > 0) return 1

            // 4. Sort remaining in alphabetical order
            return a.name.localeCompare(b.name)
        })
    }

}

decorate(injectable(), CurrentUserDepartmentsController)
decorate(inject(Api), CurrentUserDepartmentsController, 0)
decorate(inject(SessionService), CurrentUserDepartmentsController, 1)
decorate(inject(DepartmentsAccessStore), CurrentUserDepartmentsController, 2)
decorate(inject(DepartmentStore), CurrentUserDepartmentsController, 3)

export { CurrentUserDepartmentsController }
